/**
 * useSharedWithMe Hook
 *
 * Fetches documents shared with the authenticated user in a managed tenant
 * via delegated permissions (OBO flow).
 *
 * Uses an in-memory cache to prevent refetching when navigating between pages.
 */

import { useState, useEffect, useCallback } from 'react';
import { TenantApiService } from '../services/tenantApi';
import type { SharedDocument } from '../types/sharepoint';

interface UseSharedWithMeOptions {
  tenantId: string;
  limit?: number;
  enabled?: boolean;
}

interface UseSharedWithMeResult {
  documents: SharedDocument[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface CacheEntry {
  documents: SharedDocument[];
  timestamp: number;
}

// Module-level cache to persist across component mounts
const sharedDocumentsCache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Hook to fetch documents shared with the user in a managed tenant.
 *
 * @param options.tenantId - The managed tenant ID to query for shared documents
 * @param options.limit - Maximum number of documents to return (default 10, max 50)
 * @param options.enabled - Whether to fetch documents (default: true)
 * @returns Object with documents, loading state, error, and refetch function
 */
export function useSharedWithMe({
  tenantId,
  limit,
  enabled = true,
}: UseSharedWithMeOptions): UseSharedWithMeResult {
  // Create cache key that includes limit
  const cacheKey = `shared:${tenantId}:${limit ?? 'default'}`;
  const cachedEntry = tenantId ? sharedDocumentsCache.get(cacheKey) : undefined;
  const isCacheValid = cachedEntry && (Date.now() - cachedEntry.timestamp) < CACHE_TTL_MS;

  const [documents, setDocuments] = useState<SharedDocument[]>(isCacheValid ? cachedEntry.documents : []);
  const [isLoading, setIsLoading] = useState<boolean>(!isCacheValid && enabled && !!tenantId);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async (skipCache = false) => {
    // Don't fetch if disabled or no tenantId
    if (!enabled || !tenantId) {
      setDocuments([]);
      setIsLoading(false);
      return;
    }

    // Check cache first (unless skipCache is true for manual refresh)
    if (!skipCache) {
      const cached = sharedDocumentsCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS) {
        setDocuments(cached.documents);
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      const fetchedDocuments = await TenantApiService.getSharedWithMe(tenantId, limit);
      setDocuments(fetchedDocuments);
      // Update cache
      sharedDocumentsCache.set(cacheKey, { documents: fetchedDocuments, timestamp: Date.now() });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch shared documents';
      setError(errorMessage);
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, limit, enabled, cacheKey]);

  // Fetch documents on mount and when dependencies change
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Manual refetch bypasses cache
  const refetch = useCallback(async () => {
    await fetchDocuments(true);
  }, [fetchDocuments]);

  return {
    documents,
    isLoading,
    error,
    refetch,
  };
}
